import { useQuery } from "@tanstack/react-query";
import { mediumRepository } from "../repositories/mediumRepository";
import { terreiroRepository } from "../repositories/terreiroRepository";

/**
 * Query keys for terreiro inventory items and mediuns.
 */
export const itemAndMediunsQueryKeys = {
  inventoryItems: (terreiroId: string | undefined) =>
    ["terreiro", terreiroId, "inventory"] as const,
  terreiroMediuns: (terreiroId: string | undefined) =>
    ["terreiro", terreiroId, "mediuns"] as const,
  mediumById: (mediumId: string | undefined) => ["medium", mediumId] as const,
};

/**
 * Fetches all inventory items and all mediuns for a given terreiro in parallel.
 * If terreiroId is not present, and mediumId is provided, fetches the medium by id as the only member.
 * @param terreiroId The terreiro ID to fetch data for.
 * @param mediumId The medium ID to fetch data for (fallback if terreiroId is missing).
 */
export function useItemAndMediunsQueries(
  terreiroId?: string,
  mediumId?: string
) {
  // Inventory items: by terreiro or by medium fallback
  const itemsQuery = useQuery({
    queryKey:
      terreiroId
        ? itemAndMediunsQueryKeys.inventoryItems(terreiroId)
        : itemAndMediunsQueryKeys.inventoryItems(mediumId),
    queryFn: () => {
      if (terreiroId) {
        return terreiroRepository.getTerreiroInventoryItems(terreiroId);
      }
      if (mediumId) {
        // Use mediumRepository for medium inventory queries
        // Import here to avoid circular dependency if needed
        return mediumRepository.getInventoryItemsByMedium(mediumId);
      }
      return Promise.resolve([]);
    },
    enabled: !!terreiroId || !!mediumId,
    staleTime: 1000 * 60 * 5,
    gcTime: 60 * 60 * 1000,
    retry: 2,
    refetchOnMount: "always" as const,
  });

  // Mediuns: by terreiro or by mediumId fallback
  const mediunsQuery = useQuery({
    queryKey:
      terreiroId
        ? itemAndMediunsQueryKeys.terreiroMediuns(terreiroId)
        : itemAndMediunsQueryKeys.mediumById(mediumId),
    queryFn: () => {
      if (terreiroId) {
        return terreiroRepository.getTerreiroMediuns(terreiroId, {
          includeSelf: true,
        });
      }
      if (mediumId) {
        return terreiroRepository.getMediumById(mediumId);
      }
      return Promise.resolve([]);
    },
    enabled: !!terreiroId || !!mediumId,
    staleTime: 1000 * 60 * 5,
    gcTime: 60 * 60 * 1000,
    retry: 2,
    refetchOnMount: "always" as const,
  });

  return {
    items: itemsQuery.data || [],
    mediuns: mediunsQuery.data || [],
    isItemsLoading: itemsQuery.isLoading,
    isMediunsLoading: mediunsQuery.isLoading,
    isAnyLoading: itemsQuery.isLoading || mediunsQuery.isLoading,
    itemsError: itemsQuery.error,
    mediunsError: mediunsQuery.error,
    hasAnyError: !!itemsQuery.error || !!mediunsQuery.error,
    refetchAll: () => {
      itemsQuery.refetch();
      mediunsQuery.refetch();
    },
    refetchItems: itemsQuery.refetch,
    refetchMediuns: mediunsQuery.refetch,
    isSuccess: itemsQuery.isSuccess && mediunsQuery.isSuccess,
    isError: itemsQuery.isError || mediunsQuery.isError,
  };
}
